import React, {ReactElement} from "react";
import PT from "prop-types";
import clsx from "clsx";

import Sounds from "../util/Sounds.js";
import * as styles from "./styles/NavButton.module.css";
import NavButtonIcon from "./NavButtonIcon";
import NavButtonWrapper from "./NavButtonWrapper";
import UnderlineNavItem from "../../../assets/images/icons/underline-nav-item.svg";
import NavButtonTooltip, {InfoModal} from "./NavButtonTooltip";

type NavButtonClasses = {
	outer?: string;
	root?: string;
	icon?: string;
	label?: string;
	underline?: string;
};

type NavButtonProps = {
	icon?: ReactElement;
	iconName: string;
	classes?: NavButtonClasses;
	linkTo: string;
	isExternal: boolean;
	handleClick: (
		event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
		iconName: string
	) => void;
	isSelected: boolean;
	clickSound: string | string[];
	isDisabled: boolean;
	label: string;
	tooltipInfo?: InfoModal;
	onTooltipClick?: () => void;
};

const NavButton = ({
	icon,
	iconName,
	classes,
	linkTo,
	isExternal,
	handleClick,
	isSelected,
	clickSound = [],
	isDisabled,
	label,
	tooltipInfo,
	onTooltipClick,
}: NavButtonProps) => {
	const onClick = React.useCallback(
		(clickEvent: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
			const clickSoundArray = Array.isArray(clickSound)
				? clickSound
				: [clickSound];
			clickSoundArray.forEach((sound: string) => {
				Sounds.playSound(sound);
			});

			if (handleClick) {
				handleClick(clickEvent, iconName);
			}
		},
		[handleClick, clickSound, iconName]
	);

	return (
		<div
			className={clsx(
				styles?.iconOuter,
				isDisabled && styles?.disabled,
				classes?.outer
			)}
			aria-hidden={isDisabled}
		>
			<NavButtonWrapper
				className={classes?.root}
				linkTo={linkTo}
				isExternal={isExternal}
				isDisabled={isDisabled}
				onClick={onClick}
			>
				{icon || (
					<NavButtonIcon iconName={iconName} className={classes?.icon} />
				)}
				<span className={clsx(styles?.label, classes?.label)}>{label}</span>
				<UnderlineNavItem
					className={clsx(
						styles?.underline,
						classes?.underline,
						isSelected && styles?.selected
					)}
				/>
			</NavButtonWrapper>
			{tooltipInfo?.iconName === iconName && (
				<NavButtonTooltip
					onClose={onTooltipClick || (() => {})}
					{...tooltipInfo}
				/>
			)}
		</div>
	);
};

NavButton.propTypes = {
	iconName: PT.string,
	classes: PT.shape({
		outer: PT.string,
		root: PT.string,
		icon: PT.string,
		label: PT.string,
		underline: PT.string,
	}),
	linkTo: PT.string,
	isExternal: PT.bool,
	handleClick: PT.func,
	isSelected: PT.bool,
	clickSound: PT.oneOfType([PT.string, PT.array]),
	isDisabled: PT.bool,
	label: PT.string,
	infoModal: PT.shape({
		type: PT.string,
		extra: PT.string,
		iconName: PT.string,
	}),
	onTooltipClick: PT.func,
};

export default NavButton;
